import { useState } from "react";
import { testimonials as defaultTestimonials } from "../../mocks/testimonials";
import { useReveal } from "../../hooks/useReveal";

interface Testimonial {
  id: number;
  quote: string;
  author: string;
  service: string;
  image: string;
}

interface TestimonialsSectionProps {
  items?: Testimonial[];
  title?: string;
}

export default function TestimonialsSection({
  items = defaultTestimonials,
  title = "Stories From Our Families",
}: TestimonialsSectionProps) {
  const [active, setActive] = useState(0);
  const current = items[active];
  const ref = useReveal<HTMLElement>();

  return (
    <section ref={ref} className="relative bg-pk-charcoal py-28 overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #C4A87A 1px, transparent 0)", backgroundSize: "32px 32px" }} />

      {/* Large decorative quote mark */}
      <div className="absolute top-12 right-12 font-display text-[200px] text-white/5 leading-none select-none hidden md:block">
        &ldquo;
      </div>

      <div className="relative max-w-screen-xl mx-auto px-8">
        {/* Header */}
        <div className="reveal flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div>
            <span className="font-sans text-xs tracking-widest3 uppercase text-pk-gold">Client Stories</span>
            <h2 className="font-display text-5xl md:text-6xl font-medium text-white mt-4 leading-tight">
              {title}
            </h2>
          </div>
          {/* Star summary */}
          <div className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-sm px-6 py-3">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-pk-gold text-sm" />
                </div>
              ))}
            </div>
            <div>
              <p className="font-display text-2xl text-white font-semibold leading-none">5.0</p>
              <p className="font-sans text-xs text-white/40 mt-0.5">Average Rating</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-12 items-center">
          {/* Image */}
          <div className="reveal-left md:col-span-2">
            <div className="relative">
              {/* Main image */}
              <div className="w-full h-96 md:h-[520px] rounded-sm overflow-hidden img-hover-zoom">
                <img
                  src={current.image}
                  alt={current.author}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Offset border accent */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-pk-gold/20 rounded-sm -z-10" />
            </div>
          </div>

          {/* Quote */}
          <div className="reveal-right md:col-span-3">
            <div className="font-display text-9xl text-pk-gold/15 leading-none mb-2">&ldquo;</div>
            <blockquote className="font-display text-2xl md:text-3xl italic text-white/90 leading-relaxed">
              {current.quote}
            </blockquote>

            <div className="mt-8 flex items-center gap-4">
              <div className="w-12 h-px bg-pk-gold" />
              <div>
                <p className="font-sans text-sm font-semibold text-white uppercase tracking-widest">
                  {current.author}
                </p>
                <p className="font-sans text-xs text-pk-gold/70 mt-1">{current.service}</p>
              </div>
            </div>

            <div className="mt-5 flex gap-1">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-pk-gold text-sm" />
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="mt-10 flex items-center gap-4">
              <button
                onClick={() => setActive((active - 1 + items.length) % items.length)}
                className="w-12 h-12 flex items-center justify-center border border-white/15 rounded-sm hover:border-pk-gold hover:text-pk-gold transition-colors duration-300 cursor-pointer text-white/50"
              >
                <i className="ri-arrow-left-line" />
              </button>
              <button
                onClick={() => setActive((active + 1) % items.length)}
                className="w-12 h-12 flex items-center justify-center border border-white/15 rounded-sm hover:border-pk-gold hover:text-pk-gold transition-colors duration-300 cursor-pointer text-white/50"
              >
                <i className="ri-arrow-right-line" />
              </button>
              {/* Dot indicators */}
              <div className="flex gap-2 ml-2">
                {items.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    className={`cursor-pointer transition-all duration-300 rounded-full ${
                      active === i ? "w-8 h-2 bg-pk-gold" : "w-2 h-2 bg-white/20"
                    }`}
                    aria-label={`Story ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
